import {
  refScope,
  Program,
  SkillReportContentTargetsResponse,
  ActivityReportResponse,
  SkillReportResponse,
  InsightsByProgramRefScopeResponse,
  EocStudentSummaryResponse,
  EocStudentRecommendationResponse,
  EocStudentItemsResponse,
  GetCoursesByProgramInput,
  GetAvailableProgramsInput,
  ProgramCourseInput,
  ProgramAndCoursesInfo,
  CourseInfo,
  CombineScoreResponse,
  StudentTestSettingResponse,
  ProgramAndInfo,
  DownloadLink,
  PagedColumnDataResponse,
  GetUserLicensedProgramsAndProductsInput,
  ProgramAndProductsInfo,
  StudentInsightsDashboardRequest,
  StudentInsightsAccuracyOverTime,
  StudentInsightsScoreByPracticeTest,
  StudentInsightsDashboardData,
  StudentInsightsScoreByPracticeTestDetailsRequest,
  StudentInsightsScoreByPracticeTestDetails,
  StudentInsightsCompletedRequest,
  StudentInsightsCompleted,
  StudentContentLibraryOutput,
  StudentContentLibraryInput,
  GetStudentAssignmentsListRequest,
  GetAssignmentsListResponse,
  StudentSkillsOverviewSubject,
  SkillReportByProgramCourseRefScopeRequest,
  ContentReportingList,
  StudentCompletedContentGraphFilterInput,
  StudentCompletedContentListFilterInput,
  Skill,
  SkillsRequest,
  StudentPerformanceReportsResponse,
  PropertySort,
  StudentPerformanceReportListResponse
} from "../../../types/gqlTypes/graphql"
import {
  GET_ACTIVITY_REPORT_BY_REFSCOPE_PROGRAM_SUBJECT,
  GET_SKILL_REPORT_BY_CONTENT_ID,
  GET_SKILL_REPORT_BY_PROGRAM_COURSE_REF_SCOPE,
  GET_SKILL_REPORT_CONTENT_TARGETS,
  GET_STUDENT_COMPLETED_CONTENT_GRAPH,
  GET_STUDENT_COMPLETED_CONTENT_LIST,
  GET_SKILLS_LIST,
  GET_STUDENT_SKILLS_OVERVIEW,
  GET_CONTENT_DESCRIPTION,
  GET_INSIGHTS_BY_PROGRAM_REFSCOPE,
  GET_EOC_STUDENT_SUMMARY,
  GET_EOC_STUDENT_RECOMMENDATION,
  GET_EOC_STUDENT_ITEMS,
  GET_AVAILABLE_COURSES_BY_PROGRAM,
  GET_AVAILABLE_PROGRAMCOURSES,
  GET_AVAILABLE_PROGRAMS,
  GET_AVAILABLE_CONTENT_DURATION_BY_PROGRAM_COURSE,
  GET_COMBINED_ACTIVITY_REPORT_BY_REFSCOPE_PROGRAM,
  GET_COMBINED_SCORE_BY_REFSCOPE_PROGRAM,
  GET_STUDENT_TEST_SETTINGS_BY_PROGRAM_COURSE,
  GET_DOWNLOAD_LINK_PER_STUDENT_SKILL_REPORT_BY_CONTENT_ID,
  GET_PER_STUDENT_SKILL_REPORT_BY_CONTENT_ID,
  GET_USER_LICENSED_PROGRAMS_AND_PRODUCTS,
  GET_ACCURACY_OVERTIME,
  GET_INSIGHTS_SCORE_BY_PRACTICE_TEST,
  GET_INSIGHTS_DASHBOARD_METRICS,
  GET_INSIGHTS_SCORE_BY_PRACTICE_TEST_DETAILS,
  GET_STUDENT_INSIGHTS_COMPLETIONS,
  GET_STUDENT_CONTENT_LIBRARY,
  GET_STUDENT_ASSIGNMENTS,
  GET_STUDENT_PERFORMANCE_REPORT,
  GET_LIST_STUDENT_PERFORMANCE_REPORTS
} from "./reportQueries"
import { ObservableQuery } from "@apollo/client"

import {
  executeQuerySideload,
  executeWatchQuerySideload
} from "../../generic-services/helpers/studyPlan"

export async function getSkillReportContentTargets(
  program: Program,
  refScope: refScope
): Promise<SkillReportContentTargetsResponse> {
  const response = await executeQuerySideload(
    GET_SKILL_REPORT_CONTENT_TARGETS,
    {
      program,
      refScope: refScope
    }
  )
  return response.getSkillReportContentTargets
}

export function watchSkillReportByContentId(
  program: Program,
  subject: string,
  refScope: refScope,
  contentId: string
): ObservableQuery<{ getSkillReportByContentId: SkillReportResponse }> {
  return executeWatchQuerySideload(GET_SKILL_REPORT_BY_CONTENT_ID, {
    program,
    subject,
    refScope: refScope,
    contentId
  })
}

export async function getSkillReportByContentId(
  program: Program,
  subject: string,
  refScope: refScope,
  contentId: string,
  parentSkillId?: string
): Promise<SkillReportResponse> {
  const response = await executeQuerySideload(GET_SKILL_REPORT_BY_CONTENT_ID, {
    program,
    subject,
    refScope: refScope,
    contentId,
    parentSkillId
  })
  return response.getSkillReportByContentId
}

export async function getSkillReportByProgramCourseRefScope(
  data: SkillReportByProgramCourseRefScopeRequest
): Promise<SkillReportResponse> {
  const response = await executeQuerySideload(
    GET_SKILL_REPORT_BY_PROGRAM_COURSE_REF_SCOPE,
    { data }
  )
  return response.getSkillReportByProgramCourseRefScope
}

export async function getStudentSkillsOverview(
  program: Program,
  refScope: refScope,
  contentIds?: string[]
): Promise<StudentSkillsOverviewSubject[]> {
  const response = await executeQuerySideload(GET_STUDENT_SKILLS_OVERVIEW, {
    program,
    refScope,
    contentIds
  })
  return response.getStudentSkillsOverview
}

export async function getStudentCompletedContentGraph(
  data: StudentCompletedContentGraphFilterInput
): Promise<ContentReportingList> {
  const response = await executeQuerySideload(
    GET_STUDENT_COMPLETED_CONTENT_GRAPH,
    { data }
  )
  return response.getStudentCompletedContentGraph
}

export async function getStudentCompletedContentList(
  data: StudentCompletedContentListFilterInput
): Promise<ContentReportingList> {
  const response = await executeQuerySideload(
    GET_STUDENT_COMPLETED_CONTENT_LIST,
    { data }
  )
  return response.getStudentCompletedContentList
}

export async function getSkillsList(data: SkillsRequest): Promise<Skill[]> {
  const response = await executeQuerySideload(GET_SKILLS_LIST, { data })
  return response.skills?.getSkillsList ?? []
}

export async function getContentDescription(
  program: Program,
  subject: string,
  target: string,
  refScope: refScope
): Promise<string> {
  const response = await executeQuerySideload(GET_CONTENT_DESCRIPTION, {
    program,
    subject,
    target,
    refScope: refScope
  })
  return response.getContentDescription
}

export async function getActivityReportByRefscopeProgramSubject(
  program: Program,
  subject: string,
  refScope: refScope
): Promise<ActivityReportResponse> {
  const response = await executeQuerySideload(
    GET_ACTIVITY_REPORT_BY_REFSCOPE_PROGRAM_SUBJECT,
    {
      program,
      subject,
      refScope: refScope
    }
  )
  return response.getActivityReportByRefscopeProgramSubject
}

export async function getCombinedActivityReportByRefscopeProgram(
  program: Program,
  refScope: refScope
): Promise<ActivityReportResponse> {
  const response = await executeQuerySideload(
    GET_COMBINED_ACTIVITY_REPORT_BY_REFSCOPE_PROGRAM,
    {
      program,
      refScope: refScope
    }
  )
  return response.getCombinedActivityReportByRefscopeProgram
}

export function watchCombinedActivityReportByRefscopeProgram(
  program: Program,
  refScope: refScope,
  contentId?: string
): ObservableQuery<{
  getCombinedActivityReportByRefscopeProgram: ActivityReportResponse
}> {
  return executeWatchQuerySideload(
    GET_COMBINED_ACTIVITY_REPORT_BY_REFSCOPE_PROGRAM,
    {
      program,
      refScope: refScope,
      contentId
    }
  )
}

// contentId is temporary disabled
export async function getCombinedScoreByRefscopeProgram(
  program: Program,
  refScope: refScope,
  contentId?: string
): Promise<CombineScoreResponse> {
  const response = await executeQuerySideload(
    GET_COMBINED_SCORE_BY_REFSCOPE_PROGRAM,
    {
      program,
      refScope: refScope,
      contentId: contentId
    }
  )
  return response.getCombinedScoreByRefscopeProgram
}

export function watchActivityReportByRefscopeProgramSubject(
  program: Program,
  subject: string,
  refScope: refScope
): ObservableQuery<{
  getActivityReportByRefscopeProgramSubject: ActivityReportResponse
}> {
  return executeWatchQuerySideload(
    GET_ACTIVITY_REPORT_BY_REFSCOPE_PROGRAM_SUBJECT,
    {
      program,
      subject,
      refScope: refScope
    }
  )
}

export async function getInsightsByProgramRefScope(
  program: Program,
  refScope: refScope
): Promise<InsightsByProgramRefScopeResponse> {
  const response = await executeQuerySideload(
    GET_INSIGHTS_BY_PROGRAM_REFSCOPE,
    {
      program,
      refScope: refScope
    }
  )
  return response.getInsightsByProgramRefScope
}

export async function getEocStudentSummary(
  program: Program
): Promise<EocStudentSummaryResponse> {
  const response = await executeQuerySideload(GET_EOC_STUDENT_SUMMARY, {
    program
  })
  return response.getEocStudentSummary
}

export async function getInsightsAccuracyOverTime(
  data: StudentInsightsDashboardRequest
): Promise<StudentInsightsAccuracyOverTime> {
  const response = await executeQuerySideload(GET_ACCURACY_OVERTIME, {
    data
  })

  return response.getInsightsAccuracyOverTime
}

export async function getInsightsScoreByPracticeTest(
  data: StudentInsightsDashboardRequest
): Promise<StudentInsightsScoreByPracticeTest> {
  const response = await executeQuerySideload(
    GET_INSIGHTS_SCORE_BY_PRACTICE_TEST,
    {
      data
    }
  )

  return response.getInsightsScoreByPracticeTest
}

export async function getInsightsScoreByPracticeTestDetails(
  data: StudentInsightsScoreByPracticeTestDetailsRequest
): Promise<StudentInsightsScoreByPracticeTestDetails> {
  const response = await executeQuerySideload(
    GET_INSIGHTS_SCORE_BY_PRACTICE_TEST_DETAILS,
    {
      data
    }
  )

  return response.getInsightsScoreByPracticeTestDetails
}

export async function getInsightsDashboardMetrics(
  data: StudentInsightsDashboardRequest
): Promise<StudentInsightsDashboardData> {
  const response = await executeQuerySideload(GET_INSIGHTS_DASHBOARD_METRICS, {
    data
  })

  return response.getInsightsDashboardMetrics
}

export async function getStudentInsightsCompletions(
  data: StudentInsightsCompletedRequest
): Promise<StudentInsightsCompleted[]> {
  const response = await executeQuerySideload(
    GET_STUDENT_INSIGHTS_COMPLETIONS,
    {
      data
    }
  )

  return response.getStudentInsightsCompleted
}

export async function getEocStudentItems(
  program: Program,
  subject: string
): Promise<EocStudentItemsResponse> {
  const response = await executeQuerySideload(GET_EOC_STUDENT_ITEMS, {
    program,
    subject
  })
  return response.getEocStudentItems
}

export function watchEocStudentItems(
  program: Program,
  subject: string
): ObservableQuery<{ getEocStudentItems: EocStudentItemsResponse }> {
  return executeWatchQuerySideload(GET_EOC_STUDENT_ITEMS, {
    program,
    subject
  })
}

export async function getEocStudentRecommendation(
  program: Program,
  subject: string
): Promise<EocStudentRecommendationResponse> {
  const response = await executeQuerySideload(GET_EOC_STUDENT_RECOMMENDATION, {
    program,
    subject
  })
  return response.getEocStudentRecommendation
}

export async function getAvailableCoursesByProgram(
  data: GetCoursesByProgramInput
): Promise<CourseInfo[]> {
  if (!data.program) return Promise.resolve([])

  const response = await executeQuerySideload(
    GET_AVAILABLE_COURSES_BY_PROGRAM,
    {
      data
    }
  )
  return response.getAvailableCoursesByProgram
}

export async function getAvailableProgramCourses(
  data: GetAvailableProgramsInput
): Promise<ProgramAndCoursesInfo[]> {
  const response = await executeQuerySideload(GET_AVAILABLE_PROGRAMCOURSES, {
    data
  })
  return response.getAvailableProgramCourses
}

export async function getAvailablePrograms(
  data: GetAvailableProgramsInput
): Promise<ProgramAndInfo[]> {
  const response = await executeQuerySideload(GET_AVAILABLE_PROGRAMS, {
    data
  })
  return response.getAvailablePrograms
}

export async function getAvailableContentDurationByProgramCourse(
  data: ProgramCourseInput
): Promise<number> {
  const response = await executeQuerySideload(
    GET_AVAILABLE_CONTENT_DURATION_BY_PROGRAM_COURSE,
    {
      data
    }
  )
  return response.getAvailableContentDurationByProgramCourse
}

export async function getStudentTestSettingsByProgramCourse(
  data: ProgramCourseInput
): Promise<StudentTestSettingResponse> {
  const response = await executeQuerySideload(
    GET_STUDENT_TEST_SETTINGS_BY_PROGRAM_COURSE,
    {
      data
    }
  )
  return response.getStudentTestSettingsByProgramCourse
}

export async function downloadPerStudentSkillReportByContentId(
  program: Program,
  subject: string,
  refScope: refScope,
  contentId: string
): Promise<DownloadLink> {
  const response = await executeQuerySideload(
    GET_DOWNLOAD_LINK_PER_STUDENT_SKILL_REPORT_BY_CONTENT_ID,
    {
      program,
      subject,
      refScope,
      contentId
    }
  )

  return response.downloadPerStudentSkillReportByContentId
}

export async function getPerStudentSkillReportByContentId({
  program,
  subject,
  refScope,
  contentId,
  page,
  pageSize
}: {
  program: Program
  subject: string
  refScope: refScope
  contentId: string
  page: number
  pageSize: number
}): Promise<PagedColumnDataResponse> {
  const response = await executeQuerySideload(
    GET_PER_STUDENT_SKILL_REPORT_BY_CONTENT_ID,
    {
      program,
      subject,
      refScope,
      contentId,
      page,
      pageSize
    }
  )

  return response.getPerStudentSkillReportByContentId
}

export async function getUserLicensedProgramsAndProducts(
  data: GetUserLicensedProgramsAndProductsInput
): Promise<ProgramAndProductsInfo[]> {
  const response = await executeQuerySideload(
    GET_USER_LICENSED_PROGRAMS_AND_PRODUCTS,
    { data }
  )

  return response.getUserLicensedProgramsAndProducts
}

export async function getStudentContentLibrary(
  data: StudentContentLibraryInput
): Promise<StudentContentLibraryOutput> {
  const response = await executeQuerySideload(GET_STUDENT_CONTENT_LIBRARY, {
    data
  })
  return response.getStudentContentLibrary
}

export function watchStudentContentLibrary(
  data: StudentContentLibraryInput
): ObservableQuery<{
  getStudentContentLibrary: StudentContentLibraryOutput
}> {
  return executeWatchQuerySideload(GET_STUDENT_CONTENT_LIBRARY, {
    data
  })
}

export async function getStudentAssignments(
  data: GetStudentAssignmentsListRequest
): Promise<GetAssignmentsListResponse> {
  const response = await executeQuerySideload(GET_STUDENT_ASSIGNMENTS, {
    data
  })
  return response.assignments?.getStudentList
}

export async function getStudentPerformanceReport(
  contentIds: string[],
  refScope: refScope
): Promise<StudentPerformanceReportsResponse> {
  const response = await executeQuerySideload(GET_STUDENT_PERFORMANCE_REPORT, {
    contentIds,
    refScope
  })
  return response?.getStudentPerformanceReport
}

export async function getListStudentPerformanceReports(
  refScope: refScope,
  sort?: PropertySort
): Promise<StudentPerformanceReportListResponse> {
  const response = await executeQuerySideload(
    GET_LIST_STUDENT_PERFORMANCE_REPORTS,
    {
      refScope,
      sort
    }
  )
  return response?.listStudentPerformanceReports
}
